import { createContext, useContext, useState } from 'react';

const DoctorContext = createContext(null);

export const useDoctor = () => {
  const context = useContext(DoctorContext);
  if (!context) {
    throw new Error('useDoctor must be used within a DoctorProvider');
  }
  return context;
};

export const DoctorProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [doctors, setDoctors] = useState([]);

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchAllDoctors = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${apiUrl}/api/admin/doctors`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch doctors');
      }
      const transformedDoctors = data.map(doctor => ({
        id: doctor._id,
        name: doctor.doctorName,
        doctorName: doctor.doctorName,
        specialization: doctor.specialization,
        email: doctor.email,
        phone: doctor.phoneNumber,
        gender: doctor.gender,
        contactInfo: doctor.contactInformation,
        clinicName: doctor.clinicName,
        averageRating: doctor.averageRating,
        reviewCount: doctor.reviewCount,
        totalPatients: 0 // You might want to add this field in your API
      }));

      setDoctors(transformedDoctors);
      return { success: true, data: data.doctors };

      //   setDoctors(mockDoctors);
      //   return { success: true, data: mockDoctors };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // In DoctorContext.js
  const fetchDoctorDetails = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${apiUrl}/api/admin/doctors/${id}/patients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch doctor details');
      }

      const doctorBasicInfo = doctors.find(doc => doc.id === id);

      const transformedAppointments = data.map(appointment => {
        //   if (!appointment.appointmentObj.timeSlot) {
        //     console.error("Missing timeSlot for appointment:", appointment);
        //     return null; // Handle cases where timeSlot is missing
        // }
        const longitude = appointment.appointmentObj.longitude;
        const latitude = appointment.appointmentObj.latitude;
        const locationUrl = `www.google.com/maps/place/${latitude},${longitude}`;
        // const locationUrl = `https://www.google.com/maps/search/?q=${latitude},${longitude}`;
        // const [startTimes, endTimes] = appointment.appointmentObj.timeSlot.split(" - ");
        return {
          _id: appointment._id,
          patientName: appointment.appointmentObj.patient.name,
          // time: appointment.appointmentObj.timeSlot,
          startTime: appointment.appointmentObj.startTime,
          endTime: appointment.appointmentObj.endTime,
          date: new Date(appointment.appointmentObj.appointmentDate).toLocaleDateString(),
          location: locationUrl,
          latitude: latitude,
          longitude: longitude,
          // location: appointment.appointmentObj.patient.contactInformation,
          status: appointment.appointmentObj.status,
          checkIn: appointment.appointmentObj.checkIn,
          patientDetails: {
            email: appointment.appointmentObj.patient.email,
            gender: appointment.appointmentObj.patient.gender,
            phone: appointment.appointmentObj.patient.phoneNumber,
          }
        }

      }).filter(appointment => appointment !== null);

      const doctorData = {
        id: id,
        name: doctorBasicInfo?.name,
        specialization: doctorBasicInfo?.specialization,
        email: doctorBasicInfo?.email,
        phone: doctorBasicInfo?.phone,
        contactInfo: doctorBasicInfo?.contactInfo,
        appointments: transformedAppointments,
        totalPatients: transformedAppointments.length
      };

      setDoctorDetails(doctorData);
      return { success: true, data: doctorData };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return (
    <DoctorContext.Provider value={{
      loading,
      error,
      doctorDetails,
      doctors,
      fetchDoctorDetails,
      fetchAllDoctors
    }}>
      {children}
    </DoctorContext.Provider>
  );
};